import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Tooltip } from 'antd';
import debounce from 'lodash.debounce';
import { captureAnalytics } from '../../../../../actions/preload';
import { formatValidFaxNumber, validFaxNumber } from '../../../../../global/validation';
import SenderContact, { hasSavedSenderContact } from '../../../shared/SenderContact';
import './senderColumns.scss';

const SOURCES = [
  ['contact_organization', 'Saved contact'],
  ['contact_name', 'Contact person'],
  ['provider_office', 'Provider office'],
  ['caller_name', 'Caller ID'],
];

// Share the debounce across rows so sweeping over a table records one event.
const trackSenderSources = debounce((source) => {
  captureAnalytics('record_sender_sources_viewed', { source });
}, 500);

export const FaxSenderCell = ({
  sources, faxNumber, record, surface,
}) => {
  const [editorOpen, setEditorOpen] = useState(false);
  const [tooltipVisible, setTooltipVisible] = useState(false);
  const onEditorOpenChange = (open) => {
    setEditorOpen(open);
    setTooltipVisible(false);
  };
  const availableSources = SOURCES.map(([key, label]) => ({
    key, label, value: sources[key]?.trim(),
  })).filter(({ value }) => value);
  const selectedSource = availableSources[0];
  const savedContact = record && hasSavedSenderContact(record);
  const editableContact = savedContact && record.sender_contact_details.can_edit;
  const displayedFaxNumber = validFaxNumber(faxNumber).valid ? formatValidFaxNumber(faxNumber) : faxNumber;

  return (
    <>
      {selectedSource && (
        <>
          <Tooltip
            placement="top"
            visible={!editorOpen && tooltipVisible}
            onVisibleChange={setTooltipVisible}
            title={(
              <div className="record-sender-sources">
                {availableSources.map(({ key, label, value }) => (
                  <div className="record-sender-sources__row" key={key}>
                    <span className="record-sender-sources__label">{label}</span>
                    <span className="record-sender-sources__value">{value}</span>
                  </div>
                ))}
              </div>
            )}
            overlayClassName="record-sender-sources-tooltip"
            trigger={['hover', 'focus']}
          >
            {/* Saved names edit the contact; other names and the fax number keep row navigation. */}
            <span
              tabIndex={editableContact ? undefined : 0}
              onMouseEnter={() => trackSenderSources(selectedSource.key)}
              onFocus={() => trackSenderSources(selectedSource.key)}
              className="record-sender-tooltip-trigger"
            >
              {savedContact ? <SenderContact record={record} surface={surface} displayName={selectedSource.value} onEditorOpenChange={onEditorOpenChange} /> : selectedSource.value}
            </span>
          </Tooltip>
          <br />
        </>
      )}
      {savedContact && !selectedSource ? (
        <SenderContact record={record} surface={surface} displayName={displayedFaxNumber} onEditorOpenChange={onEditorOpenChange} />
      ) : displayedFaxNumber}
      {record?.sender_contact_details && !savedContact && <SenderContact record={record} surface={surface} onEditorOpenChange={onEditorOpenChange} />}
    </>
  );
};

FaxSenderCell.propTypes = {
  sources: PropTypes.shape({
    contact_organization: PropTypes.string,
    contact_name: PropTypes.string,
    provider_office: PropTypes.string,
    caller_name: PropTypes.string,
  }).isRequired,
  faxNumber: PropTypes.string,
  record: PropTypes.object,
  surface: PropTypes.string,
};

FaxSenderCell.defaultProps = { faxNumber: '', record: null, surface: 'inbox' };

// Only the redesigned tables opt in. Preserve other delivery types and cached
// rows from servers that have not yet supplied the individual name sources.
export default function buildSenderColumns(columns, surface = 'inbox') {
  return columns.map((column) => (column?.title === 'From' ? {
    ...column,
    render: (text, record, index) => {
      if (record.receiver_type === 'received_fax' && record.sender_name_sources) {
        return <FaxSenderCell sources={record.sender_name_sources} faxNumber={record.sender_email} record={record} surface={surface} />;
      }
      return column.render ? column.render(text, record, index) : text;
    },
  } : column));
}
